'use client';

import { c as _c } from "react/compiler-runtime";
import { Typography } from '@mui/material';
import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { forwardRef } from 'react';
import type { HTMLAttributes, MouseEventHandler } from 'react';
export const ClickableText = forwardRef<HTMLElement, Omit<HTMLAttributes<HTMLElement>, 'children' | 'color' | 'onClick' | 'style'> & {
  children: React.ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  variant?: TypographyProps['variant'];
  noWrap?: boolean;
  title?: string;
  className?: string;
  sx?: SxProps<Theme>;
  component?: 'span';
  /**
   * Overrides the base text color. Defaults to the variant's natural color.
   * Use for semantic color contexts (e.g. error-main for threat-state text).
   */
  baseColor?: string;
  /**
   * Overrides the hover text color.
   * Defaults to var(--mui-palette-text-secondary).
   */
  hoverColor?: string;
  'data-testid'?: string;
}>((t0, ref) => {
  const $ = _c(56);
  let baseColor;
  let children;
  let className;
  let component;
  let dataTestId;
  let hoverColor;
  let noWrap;
  let onClick;
  let rest;
  let sx;
  let title;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      onClick,
      variant,
      noWrap,
      title,
      className,
      sx,
      component,
      baseColor,
      hoverColor,
      "data-testid": dataTestId,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = baseColor;
    $[2] = children;
    $[3] = className;
    $[4] = component;
    $[5] = dataTestId;
    $[6] = hoverColor;
    $[7] = noWrap;
    $[8] = onClick;
    $[9] = rest;
    $[10] = sx;
    $[11] = title;
    $[12] = variant;
  } else {
    baseColor = $[1];
    children = $[2];
    className = $[3];
    component = $[4];
    dataTestId = $[5];
    hoverColor = $[6];
    noWrap = $[7];
    onClick = $[8];
    rest = $[9];
    sx = $[10];
    title = $[11];
    variant = $[12];
  }
  let t1;
  if ($[13] !== baseColor) {
    t1 = baseColor && {
      color: `${baseColor} !important`
    };
    $[13] = baseColor;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== t1) {
    t2 = {
      ...t1
    };
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const colorSx = t2;
  let t3;
  if ($[17] !== sx) {
    t3 = Array.isArray(sx) ? sx : [sx];
    $[17] = sx;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const consumerSx = t3;
  let t4;
  if ($[19] !== component) {
    t4 = component ? {
      component
    } : {};
    $[19] = component;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const componentProps = t4;
  let t5;
  if ($[21] !== ref) {
    t5 = element => {
      if (typeof ref === "function") {
        ref(element);
      } else {
        if (ref) {
          ref.current = element;
        }
      }
    };
    $[21] = ref;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const setElementRef = t5;
  if (!onClick) {
    let t6;
    if ($[23] !== colorSx || $[24] !== consumerSx) {
      t6 = [colorSx, ...consumerSx];
      $[23] = colorSx;
      $[24] = consumerSx;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    let t7;
    if ($[26] !== children || $[27] !== className || $[28] !== componentProps || $[29] !== dataTestId || $[30] !== noWrap || $[31] !== rest || $[32] !== setElementRef || $[33] !== t6 || $[34] !== title || $[35] !== variant) {
      t7 = <Typography {...rest} {...componentProps} ref={setElementRef} variant={variant} noWrap={noWrap} title={title} data-testid={dataTestId} className={className} sx={t6}>{children}</Typography>;
      $[26] = children;
      $[27] = className;
      $[28] = componentProps;
      $[29] = dataTestId;
      $[30] = noWrap;
      $[31] = rest;
      $[32] = setElementRef;
      $[33] = t6;
      $[34] = title;
      $[35] = variant;
      $[36] = t7;
    } else {
      t7 = $[36];
    }
    return t7;
  }
  const t6 = hoverColor ? `${hoverColor} !important` : "var(--mui-palette-text-secondary)";
  let t7;
  if ($[37] !== t6) {
    t7 = {
      color: t6
    };
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== colorSx || $[40] !== t7) {
    t8 = {
      border: "none",
      background: "none",
      padding: "0px",
      cursor: "pointer",
      textAlign: "inherit",
      textDecoration: "underline",
      ...colorSx,
      "&:hover": t7
    };
    $[39] = colorSx;
    $[40] = t7;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== consumerSx || $[43] !== t8) {
    t9 = [t8, ...consumerSx];
    $[42] = consumerSx;
    $[43] = t8;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== children || $[46] !== className || $[47] !== dataTestId || $[48] !== noWrap || $[49] !== onClick || $[50] !== rest || $[51] !== setElementRef || $[52] !== t9 || $[53] !== title || $[54] !== variant) {
    t10 = <Typography {...rest} ref={setElementRef} component="button" type="button" variant={variant} noWrap={noWrap} title={title} data-testid={dataTestId} onClick={onClick} className={className} sx={t9}>{children}</Typography>;
    $[45] = children;
    $[46] = className;
    $[47] = dataTestId;
    $[48] = noWrap;
    $[49] = onClick;
    $[50] = rest;
    $[51] = setElementRef;
    $[52] = t9;
    $[53] = title;
    $[54] = variant;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  return t10;
});
ClickableText.displayName = 'ClickableText';